import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { IgLoadingBlock } from "components/InstagramLoader";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import CloudIcon from "@mui/icons-material/Cloud";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PaymentsIcon from "@mui/icons-material/Payments";
import BalanceIcon from "@mui/icons-material/Balance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { GetCountryConversionInsightsCall } from "../../redux/api/report.api";
import {
  countryDisplayName,
  isoToFlag,
  isUnknownCountry,
} from "./countryUtils";

const CLOUD_COLORS = [
  "#7c3aed",
  "#2563eb",
  "#059669",
  "#d97706",
  "#db2777",
  "#0891b2",
  "#4f46e5",
  "#16a34a",
  "#ea580c",
  "#be185d",
];

/** Ignore tiny samples on the success tab so 1/1 does not outrank real markets. */
const MIN_REGISTRATIONS_FOR_RATE = 10;

function conversionRate(row) {
  const registrations = Number(row.registrations || 0);
  const purchases = Number(row.purchases || 0);
  if (registrations <= 0) return 0;
  if (Number.isFinite(Number(row.conversion_rate))) {
    return Number(row.conversion_rate);
  }
  return Math.min(100, Math.round((purchases / registrations) * 1000) / 10);
}

function weightForMode(row, mode) {
  const registrations = Number(row.registrations || 0);
  const purchases = Number(row.purchases || 0);
  if (mode === "registrations") return registrations;
  if (mode === "purchases") return purchases;
  if (mode === "rate") {
    if (registrations < MIN_REGISTRATIONS_FOR_RATE) return 0;
    return conversionRate(row);
  }
  return registrations + purchases * 2;
}

function fontSizeForValue(value, min, max, mode) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const maxSize = mode === "rate" ? 56 : 47;
  const minSize = mode === "rate" ? 14 : 13;
  if (max <= min) return mode === "rate" ? 36 : 22;
  const t = (value - min) / (max - min);
  const boosted = mode === "rate" ? t ** 0.72 : t;
  return Math.round(minSize + boosted * (maxSize - minSize));
}

export default function AdminStatsCountryWordCloud() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [mode, setMode] = useState("rate");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await GetCountryConversionInsightsCall({
        page: 1,
        limit: 250,
        sort: "rate",
        order: "desc",
        filter: "known",
      });
      if (cancelled) return;
      if (res && Array.isArray(res.items)) {
        setItems(res.items);
        setTotals(res.totals || null);
      } else {
        setItems([]);
        setTotals(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cloudItems = useMemo(() => {
    const weighted = items
      .filter((row) => !isUnknownCountry(row.country))
      .map((row, index) => {
        const weight = weightForMode(row, mode);
        const label = countryDisplayName(row.country);
        const rate = conversionRate(row);
        return {
          country: row.country,
          label,
          flag: isoToFlag(row.country),
          registrations: Number(row.registrations || 0),
          purchases: Number(row.purchases || 0),
          rate,
          weight,
          color: CLOUD_COLORS[index % CLOUD_COLORS.length],
        };
      })
      .filter((row) => row.weight > 0);

    if (!weighted.length) return [];

    const weights = weighted.map((r) => r.weight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);

    return weighted
      .map((row) => ({
        ...row,
        fontSize: fontSizeForValue(row.weight, min, max, mode),
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [items, mode]);

  const modeHint =
    mode === "rate"
      ? "Bigger = higher purchase success (subscriptions ÷ registrations)"
      : mode === "registrations"
        ? "Bigger = more registrations"
        : mode === "purchases"
          ? "Bigger = more active subscriptions"
          : "Bigger = registrations + 2× subscriptions";

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2.5,
        borderRadius: 3,
        borderColor: alpha(theme.palette.primary.main, 0.22),
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.95,
              )} 55%)`
            : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.14)} 0%, ${theme.palette.background.paper} 60%)`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <CloudIcon color="primary" />
              <Typography variant="h5" fontWeight={800}>
                Country reach
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Default view sizes each country by purchase success. A market with 10
              registrations and 5 subscriptions is 50% — a strong target. Switch tabs
              for raw volume.
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_e, next) => next && setMode(next)}
            sx={{
              flexWrap: "wrap",
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 2,
              p: 0.5,
            }}
          >
            <ToggleButton value="rate">
              <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Success
            </ToggleButton>
            <ToggleButton value="combined">
              <BalanceIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Combined
            </ToggleButton>
            <ToggleButton value="registrations">
              <HowToRegIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Registrations
            </ToggleButton>
            <ToggleButton value="purchases">
              <PaymentsIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Subscriptions
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`${totals?.countries ?? cloudItems.length} countries`}
          />
          <Chip
            size="small"
            variant="outlined"
            icon={<HowToRegIcon />}
            label={`${(totals?.registrations ?? 0).toLocaleString()} registrations`}
          />
          <Chip
            size="small"
            variant="outlined"
            color="success"
            icon={<PaymentsIcon />}
            label={`${(totals?.purchases ?? 0).toLocaleString()} subscriptions`}
          />
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            icon={<TrendingUpIcon />}
            label={`${Number(totals?.conversion_rate ?? 0).toLocaleString()}% overall success`}
          />
          <Chip size="small" label={modeHint} sx={{ fontWeight: 500 }} />
        </Stack>

        {loading ? (
          <IgLoadingBlock height={280} label="Loading countries…" />
        ) : cloudItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
            No country data yet.
          </Typography>
        ) : (
          <Box
            sx={{
              minHeight: 260,
              py: 2,
              px: { xs: 1, sm: 2 },
              borderRadius: 2.5,
              bgcolor: alpha(
                theme.palette.background.paper,
                theme.palette.mode === "dark" ? 0.35 : 0.72,
              ),
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
              display: "flex",
              flexWrap: "wrap",
              alignContent: "center",
              justifyContent: "center",
              gap: { xs: 0.75, sm: 1.25 },
              lineHeight: 1.1,
            }}
          >
            {cloudItems.map((row) => (
              <Tooltip
                key={row.country}
                title={`${row.flag ? `${row.flag} ` : ""}${row.label} — ${row.rate.toLocaleString()}% success · ${row.purchases.toLocaleString()} subscriptions / ${row.registrations.toLocaleString()} registrations`}
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: row.fontSize,
                    fontWeight: row.fontSize >= 32 ? 800 : 700,
                    color: row.color,
                    px: 0.4,
                    cursor: "default",
                    transition: "transform 0.15s ease, opacity 0.15s ease",
                    "&:hover": {
                      transform: "scale(1.06)",
                      opacity: 0.92,
                    },
                  }}
                >
                  {row.label}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
